import { useState, useEffect, useRef } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { House, ArrowsLeftRight, Package, ShoppingCart, Truck, ClipboardText, Gear, UsersThree, SignOut, ChartPie, DotsThreeCircle, X, Calendar, CurrencyDollar, Crown, CreditCard } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { useTenant } from "@/context/TenantContext";

// Toutes les pages de l'app
const allItems = [
  { to: "/", icon: House, label: "Tableau" },
  { to: "/mouvements", icon: ArrowsLeftRight, label: "Mouvements" },
  { to: "/stock", icon: Package, label: "Stock" },
  { to: "/commandes", icon: ShoppingCart, label: "Commandes" },
  { to: "/peremptions", icon: Calendar, label: "Péremptions" },
  { to: "/finances", icon: CurrencyDollar, label: "Finances" },
  { to: "/fournisseurs", icon: Truck, label: "Fournisseurs" },
  { to: "/categories", icon: ChartPie, label: "Catégories" },
  { to: "/inventaire", icon: ClipboardText, label: "Inventaire" },
  { to: "/utilisateurs", icon: UsersThree, label: "Utilisateurs" },
  { to: "/parametres", icon: Gear, label: "Paramètres" },
  { to: "/abonnement", icon: CreditCard, label: "Abonnement" },
  { to: "/super-admin", icon: Crown, label: "Super Admin" },
];

// Pages accessibles par rôle
const ROLE_PAGES = {
  admin: ["/", "/mouvements", "/stock", "/commandes", "/peremptions", "/finances", "/fournisseurs", "/categories", "/inventaire", "/utilisateurs", "/parametres", "/abonnement", "/super-admin"],
  assistant: ["/", "/mouvements", "/stock", "/inventaire", "/finances"],
  viewer: ["/", "/stock", "/peremptions"],
};

// Items principaux pour la bottom nav mobile (filtrés par rôle ensuite)
const MOBILE_MAIN_PATHS = ["/", "/stock", "/mouvements", "/inventaire"];

function useVisibleItems() {
  const { user } = useAuth();
  const role = user?.role || "assistant";
  const allowed = ROLE_PAGES[role] || ROLE_PAGES.assistant;
  return allItems.filter((item) => {
    if (item.to === "/super-admin") return user?.is_super_admin === true;
    return allowed.includes(item.to);
  });
}

export default function Sidebar() {
  const loc = useLocation();
  const { logout, user } = useAuth();
  const tenant = useTenant();
  const visibleItems = useVisibleItems();

  return (
    <aside
      data-testid="sidebar"
      className="hidden md:flex flex-col fixed left-0 top-0 bottom-0 w-[240px] bg-white border-r border-[color:var(--border-soft)] z-30"
    >
      <div className="px-4 py-5 border-b border-[color:var(--border-soft)] flex items-center gap-3">
        <img src={tenant.logo} alt={tenant.name} className="w-12 h-12 object-contain rounded-lg" />
        <div className="min-w-0">
          <div className="font-heading text-[15px] font-semibold text-[color:var(--text-primary)] leading-tight">
            <span className="text-[color:var(--brand)]">{tenant.shortName}</span>
          </div>
          <div className="text-[10px] uppercase tracking-[0.15em] text-[color:var(--text-muted)] mt-0.5 font-bold">
            Gestion des stocks
          </div>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
        {visibleItems.map((item) => {
          const active = loc.pathname === item.to;
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className={`flex items-center gap-3 px-4 h-14 rounded-2xl press-scale transition-colors ${
                active
                  ? "bg-[color:var(--brand)] text-white"
                  : "text-[color:var(--text-secondary)] hover:bg-[color:var(--brand-muted)]"
              }`}
            >
              <Icon size={26} weight={active ? "fill" : "regular"} />
              <span className="font-medium text-[15px]">{item.label}</span>
            </NavLink>
          );
        })}
      </nav>
      <div className="px-3 pb-2">
        <button
          onClick={logout}
          className="flex items-center gap-3 px-4 h-12 w-full rounded-2xl text-[color:var(--text-secondary)] hover:bg-red-50 hover:text-red-600 transition-colors"
        >
          <SignOut size={22} weight="regular" />
          <span className="font-medium text-[14px]">Déconnexion</span>
        </button>
      </div>
      <div className="px-6 py-4 text-xs text-[color:var(--text-muted)] border-t border-[color:var(--border-soft)]">
        {user?.name && <div className="font-medium text-[color:var(--text-secondary)] mb-1">{user.name}</div>}
        <div className="text-[10px] uppercase tracking-wider">{user?.role === "admin" ? "Administrateur" : user?.role === "viewer" ? "Lecture seule" : "Assistante"}</div>
        v1.1 — {tenant.name}
      </div>
    </aside>
  );
}

export function MobileBottomNav() {
  const loc = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  const overlayRef = useRef(null);
  const visibleItems = useVisibleItems();

  // Items principaux mobile (max 4)
  const mobileMainItems = visibleItems.filter((item) => MOBILE_MAIN_PATHS.includes(item.to)).slice(0, 4);
  // Items dans le menu "Plus"
  const mobileMoreItems = visibleItems.filter((item) => !MOBILE_MAIN_PATHS.includes(item.to));

  // Fermer le menu quand on navigue
  useEffect(() => {
    setMoreOpen(false);
  }, [loc.pathname]);

  // Fermer avec Escape
  useEffect(() => {
    if (!moreOpen) return;
    const handleKey = (e) => { if (e.key === "Escape") setMoreOpen(false); };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [moreOpen]);

  const isMoreActive = mobileMoreItems.some((item) => loc.pathname === item.to);

  // Si pas d'items "Plus", pas besoin du bouton
  const showMoreButton = mobileMoreItems.length > 0;

  return (
    <>
      {/* Overlay menu "Plus" */}
      {moreOpen && (
        <div
          ref={overlayRef}
          className="md:hidden fixed inset-0 z-40 bg-black/50 flex items-end"
          onClick={(e) => { if (e.target === overlayRef.current) setMoreOpen(false); }}
        >
          <div className="w-full bg-white rounded-t-3xl pb-24">
            <div className="flex items-center justify-between px-6 pt-5 pb-3 border-b border-[color:var(--border-soft)]">
              <span className="font-heading text-lg font-semibold">Plus</span>
              <button
                onClick={() => setMoreOpen(false)}
                className="w-10 h-10 rounded-xl flex items-center justify-center hover:bg-[color:var(--brand-muted)]"
                aria-label="Fermer"
              >
                <X size={24} />
              </button>
            </div>
            <nav className="px-4 py-3 space-y-1">
              {mobileMoreItems.map((item) => {
                const active = loc.pathname === item.to;
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    onClick={() => setMoreOpen(false)}
                    className={`flex items-center gap-4 px-4 h-14 rounded-2xl transition-colors ${
                      active
                        ? "bg-[color:var(--brand)] text-white"
                        : "text-[color:var(--text-secondary)] hover:bg-[color:var(--brand-muted)]"
                    }`}
                  >
                    <Icon size={24} weight={active ? "fill" : "regular"} />
                    <span className="font-medium text-[15px]">{item.label}</span>
                  </NavLink>
                );
              })}
            </nav>
          </div>
        </div>
      )}

      {/* Bottom nav */}
      <nav
        data-testid="bottom-nav"
        className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-[color:var(--border-soft)] z-30 flex"
      >
        {mobileMainItems.map((item) => {
          const active = loc.pathname === item.to;
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              className={`flex-1 flex flex-col items-center justify-center h-20 gap-1 ${
                active ? "text-[color:var(--brand)]" : "text-[color:var(--text-muted)]"
              }`}
            >
              <Icon size={28} weight={active ? "fill" : "regular"} />
              <span className="text-[11px] font-medium">{item.label}</span>
            </NavLink>
          );
        })}
        {/* Bouton Plus — seulement si items supplémentaires */}
        {showMoreButton && (
          <button
            onClick={() => setMoreOpen(!moreOpen)}
            className={`flex-1 flex flex-col items-center justify-center h-20 gap-1 ${
              isMoreActive || moreOpen ? "text-[color:var(--brand)]" : "text-[color:var(--text-muted)]"
            }`}
            aria-label="Plus de pages"
          >
            <DotsThreeCircle size={28} weight={isMoreActive || moreOpen ? "fill" : "regular"} />
            <span className="text-[11px] font-medium">Plus</span>
          </button>
        )}
      </nav>
    </>
  );
}
